/**
 * The discovery offer travels as `SB1:` plus unpadded base64url. Wrapping it
 * in a link to the receiver application lets any native camera application
 * route a scan straight to the receiver with the offer in the fragment, so
 * platforms without an in-page barcode API (every iOS browser) bootstrap
 * without a second optical acquisition. The fragment never reaches a server,
 * and the offer is public by design: session security rests on the key
 * agreement and the physical channel confirmations, never on link secrecy.
 *
 * Forks and self-hosted deployments change this one constant.
 */
export const RECEIVER_APP_URL = 'https://spectrabus-rx.thast.live';

export function encodeBootstrapLink(bootstrapText: string): string {
  return `${RECEIVER_APP_URL}/#${bootstrapText}`;
}

/**
 * Extracts the canonical `SB1:` bootstrap text from either encoding: the
 * raw text itself, or a receiver link carrying it in the fragment. Returns
 * null when neither shape is present; strict validation of the payload
 * stays with the offer decoder.
 */
export function extractBootstrapText(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.startsWith('SB1:')) return trimmed;
  const fragmentIndex = trimmed.indexOf('#SB1:');
  if (fragmentIndex >= 0) return trimmed.slice(fragmentIndex + 1);
  return null;
}

const BASE64_URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function base64UrlEncodeBytes(bytes: Uint8Array): string {
  let encoded = '';
  for (let offset = 0; offset < bytes.length; offset += 3) {
    const first = bytes[offset] ?? 0;
    const second = bytes[offset + 1] ?? 0;
    const third = bytes[offset + 2] ?? 0;
    encoded += BASE64_URL_ALPHABET.charAt(first >>> 2);
    encoded += BASE64_URL_ALPHABET.charAt(((first & 0x03) << 4) | (second >>> 4));
    if (offset + 1 < bytes.length) {
      encoded += BASE64_URL_ALPHABET.charAt(((second & 0x0f) << 2) | (third >>> 6));
    }
    if (offset + 2 < bytes.length) {
      encoded += BASE64_URL_ALPHABET.charAt(third & 0x3f);
    }
  }
  return encoded;
}

/**
 * The optical return path: any return payload (key response, confirmation
 * or sealed rank feedback) rendered as `SB1R:` plus unpadded base64url in a
 * QR on the receiver's display, scanned by the sender's camera. The engines
 * are transport-agnostic, so this carries exactly the bytes the acoustic
 * return band would; a transfer completes with sound disabled entirely.
 */
export function encodeReturnText(payload: Uint8Array): string {
  return `SB1R:${base64UrlEncodeBytes(payload)}`;
}

export function decodeReturnText(value: string): Uint8Array | null {
  const trimmed = value.trim();
  if (!trimmed.startsWith('SB1R:')) return null;
  const encoded = trimmed.slice(5);
  if (encoded === '' || !/^[A-Za-z\d_-]+$/u.test(encoded)) return null;
  const paddingLength = (4 - (encoded.length % 4)) % 4;
  if (paddingLength === 3) return null;
  try {
    const binary = atob(
      `${encoded.replace(/-/gu, '+').replace(/_/gu, '/')}${'='.repeat(paddingLength)}`,
    );
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    return null;
  }
}
